// ScheduledTextsTab — Phase 1.3 surface on /notifications.
// Shows pending scheduled texts the current user owns (today these come from
// Books' Collections chase), with Cancel + Edit (body + reschedule). Realtime-subscribed so changes
// propagate without a refresh.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import type { TeamMember } from "../../lib/types";

interface ScheduledText {
  id: string;
  deal_id: string;
  user_id: string;
  scheduled_for: string;
  body: string;
  to_phone: string | null;
  status: string;
  created_at: string;
}

interface DealPreview {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
}

interface Props {
  member: TeamMember | null;
}

function fmtRelative(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diff = d - now;
  const abs = Math.abs(diff);
  const min = Math.round(abs / 60_000);
  const hr = Math.round(abs / 3_600_000);
  const day = Math.round(abs / 86_400_000);
  const direction = diff > 0 ? "in" : "ago";
  if (abs < 60_000) return diff > 0 ? "in <1 min" : "<1 min ago";
  if (min < 60) return `${direction} ${min}m`;
  if (hr < 24) return `${direction} ${hr}h`;
  return `${direction} ${day}d`;
}

function fmtAbsolute(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function isoToLocalInput(iso: string): string {
  // Convert ISO timestamp to the format <input type="datetime-local"> expects: YYYY-MM-DDTHH:MM
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string {
  // browser gives us local time without TZ; treat as local and convert to ISO
  return new Date(local).toISOString();
}

export default function ScheduledTextsTab({ member }: Props) {
  const [rows, setRows] = useState<ScheduledText[]>([]);
  const [deals, setDeals] = useState<Record<string, DealPreview>>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [editWhen, setEditWhen] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  async function load() {
    if (!member?.id) { setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from("claude_scheduled_texts")
      .select("id, deal_id, user_id, scheduled_for, body, to_phone, status, created_at")
      .eq("user_id", member.id)
      .eq("status", "pending")
      .order("scheduled_for", { ascending: true });
    if (error) {
      setErrMsg(`couldn't load: ${error.message}`);
      setLoading(false);
      return;
    }
    const list = (data ?? []) as ScheduledText[];
    setRows(list);
    // Fetch deal previews
    const dealIds = Array.from(new Set(list.map((r) => r.deal_id)));
    if (dealIds.length > 0) {
      const { data: dealRows } = await supabase
        .from("deals")
        .select("id, homeowner_name, property_street")
        .in("id", dealIds);
      const map: Record<string, DealPreview> = {};
      for (const d of (dealRows ?? []) as DealPreview[]) map[d.id] = d;
      setDeals(map);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
    if (!member?.id) return;
    // Realtime: pick up new schedules + status changes (sent/cancelled)
    const channel = supabase
      .channel("scheduled-texts-mine")
      .on("postgres_changes",
        { event: "*", schema: "public", table: "claude_scheduled_texts", filter: `user_id=eq.${member.id}` },
        () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  async function cancel(id: string) {
    if (!confirm("Cancel this scheduled text? The homeowner won't get it.")) return;
    setBusy(id);
    setErrMsg(null);
    const { data, error } = await supabase.rpc("claude_scheduled_text_cancel", { p_id: id });
    setBusy(null);
    if (error || !data) {
      setErrMsg(error?.message ?? "Cancel failed (you may not own this row, or it already sent)");
      return;
    }
    await load();
  }

  function startEdit(r: ScheduledText) {
    setEditing(r.id);
    setEditBody(r.body);
    setEditWhen(isoToLocalInput(r.scheduled_for));
    setErrMsg(null);
  }

  async function saveEdit(id: string) {
    if (!editBody.trim()) { setErrMsg("Body can't be empty"); return; }
    if (!editWhen) { setErrMsg("Pick a send time"); return; }
    const dueIso = localInputToIso(editWhen);
    if (new Date(dueIso).getTime() < Date.now() + 60_000) {
      setErrMsg("Pick at least 1 minute in the future");
      return;
    }
    setBusy(id);
    const { data, error } = await supabase.rpc("claude_scheduled_text_update", {
      p_id: id, p_body: editBody.trim(), p_scheduled_for: dueIso,
    });
    setBusy(null);
    if (error || !data) {
      setErrMsg(error?.message ?? "Update failed (already sent or out of window?)");
      return;
    }
    setEditing(null);
    await load();
  }

  const empty = !loading && rows.length === 0;
  const sortedRows = useMemo(() => rows, [rows]);

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500 mb-2 px-1">
        Texts scheduled to go out from your name, waiting to send. Cancel any time before they go out, or edit the message body and reschedule.
      </div>
      {errMsg && (
        <div className="bg-red-50 border border-red-200 rounded p-2.5 text-xs text-red-800">{errMsg}</div>
      )}
      {loading && <div className="text-sm text-slate-400 px-3 py-6">Loading…</div>}
      {empty && (
        <div className="text-sm text-slate-400 px-3 py-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
          No scheduled texts. Open any deal and ask 🤠 Greg: <em className="text-slate-600">"schedule a text for tomorrow at 9am: …"</em>
        </div>
      )}
      {sortedRows.map((r) => {
        const deal = deals[r.deal_id];
        const homeowner = deal?.homeowner_name ?? "(unknown deal)";
        const street = deal?.property_street ?? "";
        const isEditing = editing === r.id;
        const isBusy = busy === r.id;
        return (
          <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <Link to={`/deal/${r.deal_id}`} className="text-sm font-semibold text-bardin-ink hover:text-bardin-orange">
                  {homeowner}
                </Link>
                {street && <div className="text-xs text-slate-500">{street}</div>}
                <div className="text-xs text-slate-400 mt-0.5">
                  → <span className="font-mono">{r.to_phone ?? "(no phone on file)"}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-bardin-orange">{fmtRelative(r.scheduled_for)}</div>
                <div className="text-[10px] text-slate-400">{fmtAbsolute(r.scheduled_for)}</div>
              </div>
            </div>

            {!isEditing ? (
              <>
                <div className="bg-slate-50 rounded p-2 text-sm text-slate-800 mb-2 whitespace-pre-wrap break-words">{r.body}</div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => startEdit(r)}
                    disabled={isBusy}
                    className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => cancel(r.id)}
                    disabled={isBusy}
                    className="px-2.5 py-1 text-xs font-medium rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                  >
                    {isBusy ? "Cancelling…" : "Cancel"}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={3}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-bardin-orange focus:ring-1 focus:ring-bardin-orange"
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500 flex-shrink-0">Send at:</label>
                  <input
                    type="datetime-local"
                    value={editWhen}
                    onChange={(e) => setEditWhen(e.target.value)}
                    className="px-2 py-1 border border-slate-300 rounded text-xs focus:outline-none focus:border-bardin-orange"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => setEditing(null)}
                    disabled={isBusy}
                    className="px-2.5 py-1 text-xs font-medium rounded text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveEdit(r.id)}
                    disabled={isBusy}
                    className="px-3 py-1 text-xs font-bold rounded bg-bardin-orange text-white hover:bg-bardin-orange/90 disabled:opacity-50"
                  >
                    {isBusy ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
